// ─── Project export to PDF / Word / PowerPoint ──────────────────────────
// All formats are generated client-side. No server round-trip required.

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PptxGenJS from "pptxgenjs";
import {
  Document, Packer, Paragraph, Table, TableRow, TableCell,
  TextRun, HeadingLevel, AlignmentType, WidthType, BorderStyle,
  ImageRun,
} from "docx";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";

// ─── helpers ─────────────────────────────────────────────────────────────
const fmtMoney = (n) => "$" + Number(n || 0).toLocaleString();
const todayStr = () => new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
const safeName = (s) => (s || "project").replace(/[^a-z0-9-]+/gi, "_").replace(/^_+|_+$/g, "");

const COLORS = {
  primary: "#0369A1", primaryRGB: [3, 105, 161],
  accent:  "#06B6D4",
  text:    "#0F172A",
  muted:   "#64748B",
  green:   "#16A34A",
  amber:   "#D97706",
  red:     "#DC2626",
  border:  "#E0F2FE",
};

// ─── Capture graphical sections ─────────────────────────────────────────
// Snapshots every element with `data-export-capture` currently in the DOM.
// Tabs unmount when you switch, so only the currently-visible tab's charts
// are captured — that's by design: export from the tab whose visuals you
// want included.
async function captureCharts() {
  const els = Array.from(document.querySelectorAll("[data-export-capture]"));
  const out = [];
  for (const el of els) {
    try {
      // Skip elements that aren't actually rendered (display:none, 0×0).
      const rect = el.getBoundingClientRect();
      if (rect.width < 50 || rect.height < 50) continue;
      const canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
        useCORS: true,
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });
      out.push({
        id: el.dataset.exportCapture,
        title: el.dataset.exportTitle || el.dataset.exportCapture,
        dataUrl: canvas.toDataURL("image/png"),
        w: canvas.width,
        h: canvas.height,
      });
    } catch (e) {
      // One failed chart shouldn't break the whole export.
      // eslint-disable-next-line no-console
      console.warn("export: failed to capture", el.dataset.exportCapture, e);
    }
  }
  return out;
}

// Build a normalized "report" object that all three exporters consume.
export function buildReport(doc) {
  const m = doc.meta || {};
  const tasks = doc.tasks || [];
  const reqs  = doc.requirements || [];
  const raids = doc.raids || [];
  const acts  = doc.actions || [];
  const bud   = doc.budget || [];
  const stks  = doc.stakeholders || [];
  const ms    = doc.milestones || [];
  const gaps  = doc.gaps || [];
  const ai    = doc.aiOutputs || {};

  const totalAlloc = bud.reduce((s, b) => s + (b.allocated || 0), 0);
  const totalSpent = bud.reduce((s, b) => s + (b.actual    || 0), 0);
  const totalFcst  = bud.reduce((s, b) => s + (b.forecast  || 0), 0);
  const avgPct = tasks.length ? Math.round(tasks.reduce((s, t) => s + (t.pct || 0), 0) / tasks.length) : 0;

  return {
    title: m.name || "Project Report",
    code: m.code || "",
    meta: m,
    kpis: {
      progress:   avgPct,
      health:     m.health || "—",
      phase:      m.phase || "—",
      openRisks:  raids.filter(r => r.type === "Risk"  && r.status !== "Closed").length,
      openIssues: raids.filter(r => r.type === "Issue" && r.status !== "Closed").length,
      criticalRisks: raids.filter(r => r.type === "Risk" && r.score === "Critical" && r.status !== "Closed").length,
      reqsDone:   reqs.filter(r => r.status === "Completed").length,
      reqsTotal:  reqs.length,
      actsOpen:   acts.filter(a => a.status !== "Completed").length,
      actsOverdue: acts.filter(a => a.status === "Overdue").length,
      milestonesDone: ms.filter(x => x.status === "Completed").length,
      milestonesTotal: ms.length,
      totalAlloc, totalSpent, totalFcst,
      variance: totalFcst - totalAlloc,
      methodology: m.methodology || "Waterfall",
      sponsor: m.sponsor || "—",
      pm: m.pm || "—",
    },
    summary: [
      ["Phase",        m.phase  || "—"],
      ["Health",       m.health || "—"],
      ["Methodology",  m.methodology || "Waterfall"],
      ["Sponsor",      m.sponsor || "—"],
      ["PM",           m.pm     || "—"],
      ["Start",        m.start  || "—"],
      ["End",          m.end    || "—"],
      ["Budget",       fmtMoney(m.budget)],
      ["Overall Task Progress", `${avgPct}%`],
      ["Open Risks",   raids.filter(r => r.type === "Risk"  && r.status !== "Closed").length],
      ["Open Issues",  raids.filter(r => r.type === "Issue" && r.status !== "Closed").length],
      ["Requirements", `${reqs.filter(r => r.status === "Completed").length} / ${reqs.length} completed`],
      ["Budget Spent", `${fmtMoney(totalSpent)} of ${fmtMoney(totalAlloc)} · Forecast ${fmtMoney(totalFcst)}`],
    ],
    sections: [
      {
        title: "Tasks & Gantt",
        headers: ["ID", "Task", "Start", "End", "%", "Critical"],
        rows: tasks.map(t => [t.id, t.name, t.start, t.end, `${t.pct || 0}%`, t.critical ? "Yes" : ""]),
      },
      {
        title: "Milestones",
        headers: ["Name", "Date", "Status"],
        rows: ms.map(x => [x.name, x.date, x.status]),
      },
      {
        title: "Requirements",
        headers: ["ID", "Title", "Category", "Priority", "Status", "Owner"],
        rows: reqs.map(r => [r.id, r.title, r.category, r.priority, r.status, r.owner]),
      },
      {
        title: "Gap / Fit Analysis",
        headers: ["Area", "Req", "Fit", "Gap", "Impact", "Priority", "Owner", "Status"],
        rows: gaps.map(g => [g.area, g.requirement, g.fitStatus, g.gap, g.impact, g.priority, g.owner, g.status]),
      },
      {
        title: "RAID Log",
        headers: ["Ref", "Type", "Title", "Probability", "Impact", "Owner", "Status"],
        rows: raids.map(r => [r.ref, r.type, r.title, r.probability, r.impact, r.owner, r.status]),
      },
      {
        title: "Action Items",
        headers: ["ID", "Title", "Assignee", "Priority", "Status", "Due"],
        rows: acts.map(a => [a.id, a.title, a.assignee, a.priority, a.status, a.due]),
      },
      {
        title: "Budget",
        headers: ["Category", "Allocated", "Actual", "Forecast", "Variance"],
        rows: bud.map(b => [b.category, fmtMoney(b.allocated), fmtMoney(b.actual), fmtMoney(b.forecast), fmtMoney((b.forecast || 0) - (b.allocated || 0))]),
        totals: ["TOTAL", fmtMoney(totalAlloc), fmtMoney(totalSpent), fmtMoney(totalFcst), fmtMoney(totalFcst - totalAlloc)],
      },
      {
        title: "Stakeholders",
        headers: ["Name", "Title", "Role", "Dept", "Influence", "Interest", "Engagement"],
        rows: stks.map(s => [s.name, s.title || "", s.role, s.dept, s.influence, s.interest, s.engagement]),
      },
    ],
    aiNarratives: [
      ["Executive Status",     ai.exec?.text],
      ["Risk Analysis",        ai.risk?.text],
      ["Requirements Analysis",ai.reqAnalysis?.text],
      ["Action Digest",        ai.actions?.text],
      ["Budget Narrative",     ai.budget?.text],
      ["Gap Analysis",         ai.gap?.text],
      ["S/4HANA Executive Brief", ai.s4hana?.text],
    ].filter(([_, t]) => t),
  };
}

// ─── PDF helpers ─────────────────────────────────────────────────────────
// Full-page section divider: big number tag on the left, section title
// centered vertically, subtitle beneath. Used between major sections so the
// deck reads like a magazine, not a data dump.
function drawSectionDivider(pdf, pageW, pageH, tag, title, subtitle) {
  pdf.setFillColor(248, 250, 252);
  pdf.rect(0, 0, pageW, pageH, "F");
  // Left ocean band
  pdf.setFillColor(...[3, 105, 161]);
  pdf.rect(0, 0, 44, pageH, "F");
  // Cyan accent
  pdf.setFillColor(6, 182, 212);
  pdf.rect(44, 0, 6, pageH, "F");
  // Number tag
  pdf.setFont("helvetica", "bold").setFontSize(80).setTextColor(...[3, 105, 161]);
  pdf.text(tag, 80, pageH / 2 - 20);
  // Rule line
  pdf.setDrawColor(...[3, 105, 161]);
  pdf.setLineWidth(2);
  pdf.line(80, pageH / 2, 260, pageH / 2);
  // Title
  pdf.setFont("helvetica", "bold").setFontSize(28).setTextColor(15, 23, 42);
  const wrapped = pdf.splitTextToSize(title, pageW - 120);
  let ty = pageH / 2 + 40;
  wrapped.slice(0, 2).forEach(l => { pdf.text(l, 80, ty); ty += 32; });
  // Subtitle
  if (subtitle) {
    pdf.setFont("helvetica", "normal").setFontSize(11).setTextColor(100, 116, 139);
    pdf.text(subtitle, 80, ty + 4);
  }
  // Footer stamp
  pdf.setFont("helvetica", "normal").setFontSize(8).setTextColor(148, 163, 184);
  pdf.text("HARMONIZER · capittance.com", pageW - 40, pageH - 30, { align: "right", charSpace: 2 });
}

// KPI Infographic: 6 KPI tiles in a 3×2 grid + a budget bar row + summary
// footer. Vector-only (no fonts beyond helvetica) so it prints cleanly at any
// resolution.
function drawKpiInfographic(pdf, pageW, pageH, r) {
  const k = r.kpis;
  // Header band
  pdf.setFillColor(...[3, 105, 161]);
  pdf.rect(0, 0, pageW, 90, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold").setFontSize(9);
  pdf.text("EXECUTIVE SNAPSHOT", 40, 40, { charSpace: 3 });
  pdf.setFont("helvetica", "bold").setFontSize(22);
  pdf.text(r.title, 40, 68);

  // 6 KPI tiles — 3 across, 2 down.
  const tiles = [
    { label: "OVERALL PROGRESS", value: `${k.progress}%`,                            sub: `${k.methodology} delivery`, color: [3, 105, 161] },
    { label: "PROJECT HEALTH",   value: k.health.toUpperCase(),                       sub: `Phase · ${k.phase}`,       color: healthColor(k.health) },
    { label: "OPEN RISKS",       value: String(k.openRisks),                          sub: `${k.criticalRisks} critical`, color: [220, 38, 38] },
    { label: "REQUIREMENTS",     value: `${k.reqsDone}/${k.reqsTotal}`,               sub: `${pct(k.reqsDone, k.reqsTotal)}% complete`, color: [37, 99, 235] },
    { label: "OPEN ACTIONS",     value: String(k.actsOpen),                           sub: `${k.actsOverdue} overdue`, color: [217, 119, 6] },
    { label: "MILESTONES",       value: `${k.milestonesDone}/${k.milestonesTotal}`,   sub: `${pct(k.milestonesDone, k.milestonesTotal)}% delivered`, color: [22, 163, 74] },
  ];
  const startY = 120;
  const gap = 14;
  const tileW = (pageW - 80 - gap * 2) / 3;
  const tileH = 120;
  tiles.forEach((t, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = 40 + col * (tileW + gap);
    const y = startY + row * (tileH + gap);
    // Card body
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.8);
    pdf.roundedRect(x, y, tileW, tileH, 6, 6, "FD");
    // Accent bar left
    pdf.setFillColor(...t.color);
    pdf.roundedRect(x, y, 4, tileH, 2, 2, "F");
    // Label
    pdf.setFont("helvetica", "bold").setFontSize(8).setTextColor(100, 116, 139);
    pdf.text(t.label, x + 14, y + 22, { charSpace: 2 });
    // Value
    pdf.setFont("helvetica", "bold").setFontSize(28).setTextColor(...t.color);
    pdf.text(t.value, x + 14, y + 60);
    // Sub
    pdf.setFont("helvetica", "normal").setFontSize(9).setTextColor(71, 85, 105);
    pdf.text(t.sub, x + 14, y + tileH - 16);
  });

  // Budget bar row
  const barY = startY + 2 * (tileH + gap) + 12;
  pdf.setFont("helvetica", "bold").setFontSize(11).setTextColor(15, 23, 42);
  pdf.text("BUDGET", 40, barY, { charSpace: 2 });
  pdf.setFont("helvetica", "normal").setFontSize(9).setTextColor(100, 116, 139);
  pdf.text(`Allocated ${fmtMoney(k.totalAlloc)}  ·  Spent ${fmtMoney(k.totalSpent)}  ·  Forecast ${fmtMoney(k.totalFcst)}  ·  Variance ${fmtMoney(k.variance)}`, 40, barY + 14);
  // Bar
  const barX = 40, barW = pageW - 80, barH = 18;
  const barBaseY = barY + 26;
  pdf.setFillColor(240, 249, 255);
  pdf.roundedRect(barX, barBaseY, barW, barH, 4, 4, "F");
  const spentPct = k.totalAlloc > 0 ? Math.min(1, k.totalSpent / k.totalAlloc) : 0;
  const fcstPct  = k.totalAlloc > 0 ? Math.min(1.2, k.totalFcst  / k.totalAlloc) : 0;
  pdf.setFillColor(...(k.variance > 0 ? [220, 38, 38] : [3, 105, 161]));
  pdf.roundedRect(barX, barBaseY, Math.max(2, barW * spentPct), barH, 4, 4, "F");
  // Forecast marker
  pdf.setDrawColor(217, 119, 6);
  pdf.setLineWidth(2);
  pdf.line(barX + barW * fcstPct, barBaseY - 4, barX + barW * fcstPct, barBaseY + barH + 4);

  // Footer
  pdf.setFont("helvetica", "normal").setFontSize(8).setTextColor(148, 163, 184);
  pdf.text(`Generated ${todayStr()}  ·  ${r.code || "—"}  ·  HARMONIZER · capittance.com`, pageW / 2, pageH - 30, { align: "center", charSpace: 1 });
}

function pct(a, b) { return b > 0 ? Math.round((a / b) * 100) : 0; }
function healthColor(h) {
  const m = { "On Track": [22, 163, 74], "At Risk": [217, 119, 6], "Off Track": [220, 38, 38], "On Hold": [100, 116, 139] };
  return m[h] || [3, 105, 161];
}
function healthHex(h) {
  const m = { "On Track": "16A34A", "At Risk": "D97706", "Off Track": "DC2626", "On Hold": "64748B" };
  return m[h] || "0369A1";
}

// PPT section divider slide: mirrors the PDF divider — ocean band on the left,
// oversized numeric tag, section title, subtitle. Used between major sections.
function addPptSectionDivider(ppt, tag, title, subtitle) {
  const SLIDE_W = 13.333, SLIDE_H = 7.5;
  const slide = ppt.addSlide();
  slide.background = { color: "F8FAFC" };
  slide.addShape(ppt.ShapeType.rect, { x: 0,    y: 0, w: 0.75, h: SLIDE_H, fill: { color: "0369A1" }, line: { color: "0369A1" } });
  slide.addShape(ppt.ShapeType.rect, { x: 0.75, y: 0, w: 0.1,  h: SLIDE_H, fill: { color: "06B6D4" }, line: { color: "06B6D4" } });
  slide.addText(tag, { x: 1.4, y: 2.6, w: 4, h: 1.8, fontSize: 130, bold: true, color: "0369A1", valign: "middle" });
  slide.addShape(ppt.ShapeType.line, { x: 1.4, y: 4.35, w: 3.0, h: 0, line: { color: "0369A1", width: 2 } });
  slide.addText(title, { x: 1.4, y: 4.6, w: 11.5, h: 1.0, fontSize: 40, bold: true, color: "0F172A" });
  if (subtitle) slide.addText(subtitle, { x: 1.4, y: 5.7, w: 11.5, h: 0.4, fontSize: 14, color: "64748B" });
  slide.addText("HARMONIZER · capittance.com", { x: 0, y: SLIDE_H - 0.4, w: SLIDE_W - 0.4, h: 0.3, fontSize: 9, align: "right", color: "94A3B8", charSpacing: 2 });
}

// ─── PDF ──────────────────────────────────────────────────────────────────
export async function exportPDF(doc) {
  const r = buildReport(doc);
  const charts = await captureCharts();
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const k = r.kpis;

  // ─── Magazine-grade cover page ────────────────────────────────────────
  // Deep-ocean full-bleed band, oversized hero mark, project title,
  // methodology + generated stamp. This is the first impression sponsors
  // see when they open the deck — treat it like a magazine cover.
  pdf.setFillColor(...COLORS.primaryRGB);
  pdf.rect(0, 0, pageW, pageH, "F");
  // Accent stripe (right edge) in cyan for depth.
  pdf.setFillColor(6, 182, 212);
  pdf.rect(pageW - 12, 0, 12, pageH, "F");
  // Hero mark — hollow "CAPITTANCE" wordmark near the top.
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold").setFontSize(10);
  pdf.text("HARMONIZER", 40, 60, { charSpace: 4 });
  pdf.setFont("helvetica", "normal").setFontSize(8).setTextColor(186, 230, 253);
  pdf.text("THE SAP DELIVERY OPERATING SYSTEM · capittance.com", 40, 76, { charSpace: 1 });

  // Massive title mid-page
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold").setFontSize(38);
  const titleLines = pdf.splitTextToSize(r.title.toUpperCase(), pageW - 100);
  let ty = pageH * 0.42;
  titleLines.slice(0, 3).forEach(line => { pdf.text(line, 40, ty); ty += 42; });

  // Project code + methodology pills row
  pdf.setFont("helvetica", "bold").setFontSize(10).setTextColor(103, 232, 249);
  pdf.text(r.code, 40, ty + 14, { charSpace: 2 });
  pdf.setFont("helvetica", "normal").setFontSize(10).setTextColor(186, 230, 253);
  pdf.text(`·  ${k.methodology.toUpperCase()}  ·  ${k.phase.toUpperCase()}`, 40 + pdf.getTextWidth(r.code) + 8, ty + 14, { charSpace: 2 });

  // Bottom band with sponsor / PM / date
  pdf.setFillColor(15, 23, 42);
  pdf.rect(0, pageH - 88, pageW, 88, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold").setFontSize(9);
  pdf.text("SPONSOR", 40, pageH - 60, { charSpace: 2 });
  pdf.text("PROJECT MANAGER", pageW / 3 + 20, pageH - 60, { charSpace: 2 });
  pdf.text("GENERATED", (pageW / 3) * 2 + 20, pageH - 60, { charSpace: 2 });
  pdf.setFont("helvetica", "normal").setFontSize(12).setTextColor(186, 230, 253);
  pdf.text(k.sponsor, 40, pageH - 40);
  pdf.text(k.pm, pageW / 3 + 20, pageH - 40);
  pdf.text(todayStr(), (pageW / 3) * 2 + 20, pageH - 40);

  // ─── KPI Infographic page ─────────────────────────────────────────────
  pdf.addPage();
  drawSectionDivider(pdf, pageW, pageH, "01", "Executive Snapshot", "Portfolio KPIs at a glance");
  pdf.addPage();
  drawKpiInfographic(pdf, pageW, pageH, r);

  // ─── Executive Summary table ──────────────────────────────────────────
  pdf.addPage();
  drawSectionDivider(pdf, pageW, pageH, "02", "Executive Summary", "Project meta and rolled-up figures");
  pdf.addPage();
  pdf.setTextColor(15, 23, 42);
  pdf.setFont("helvetica", "bold").setFontSize(18);
  pdf.text("Executive Summary", 40, 60);
  pdf.setDrawColor(...COLORS.primaryRGB);
  pdf.setLineWidth(2);
  pdf.line(40, 70, 140, 70);
  autoTable(pdf, {
    startY: 90,
    head: [["Field", "Value"]],
    body: r.summary,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 6 },
    headStyles: { fillColor: COLORS.primaryRGB, textColor: 255, fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 160, fontStyle: "bold", textColor: [3, 105, 161] } },
    margin: { left: 40, right: 40 },
  });

  // ─── Visual highlights (captured charts) ──────────────────────────────
  if (charts.length) {
    pdf.addPage();
    drawSectionDivider(pdf, pageW, pageH, "03", "Visual Highlights", "Charts captured live from the workspace");
    charts.forEach((c, idx) => {
      pdf.addPage();
      pdf.setFont("helvetica", "bold").setFontSize(16).setTextColor(15, 23, 42);
      pdf.text(c.title || `Visual ${idx + 1}`, 40, 50);
      pdf.setDrawColor(...COLORS.primaryRGB);
      pdf.setLineWidth(1.5);
      pdf.line(40, 58, 100, 58);
      pdf.setFont("helvetica", "italic").setFontSize(8).setTextColor(100, 116, 139);
      pdf.text("Captured live from the workspace", 40, 72);
      const maxW = pageW - 80;
      const maxH = pageH - 120;
      let drawW = maxW;
      let drawH = (c.h / c.w) * drawW;
      if (drawH > maxH) { drawH = maxH; drawW = (c.w / c.h) * drawH; }
      pdf.addImage(c.dataUrl, "PNG", 40, 90, drawW, drawH);
    });
  }

  // ─── Sections (each preceded by a divider) ────────────────────────────
  const sectionOrderStart = charts.length ? 4 : 3;
  r.sections.forEach((sec, i) => {
    if (!sec.rows.length) return;
    pdf.addPage();
    drawSectionDivider(pdf, pageW, pageH, String(sectionOrderStart + i).padStart(2, "0"), sec.title, `${sec.rows.length} rows`);
    pdf.addPage();
    pdf.setFont("helvetica", "bold").setFontSize(18).setTextColor(15, 23, 42);
    pdf.text(sec.title, 40, 60);
    pdf.setDrawColor(...COLORS.primaryRGB);
    pdf.setLineWidth(2);
    pdf.line(40, 70, 140, 70);
    autoTable(pdf, {
      startY: 90,
      head: [sec.headers],
      body: sec.rows,
      foot: sec.totals ? [sec.totals] : undefined,
      theme: "striped",
      styles:     { fontSize: 8, cellPadding: 5, overflow: "linebreak", valign: "top" },
      headStyles: { fillColor: COLORS.primaryRGB, textColor: 255, fontStyle: "bold" },
      footStyles: { fillColor: [240, 249, 255], textColor: COLORS.primaryRGB, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });
  });

  // AI narratives
  if (r.aiNarratives.length) {
    pdf.addPage();
    pdf.setFont("helvetica", "bold").setFontSize(14).setTextColor(15, 23, 42);
    pdf.text("Executive Insights", 40, 50);
    let y = 70;
    r.aiNarratives.forEach(([title, text]) => {
      if (y > 720) { pdf.addPage(); y = 50; }
      pdf.setFont("helvetica", "bold").setFontSize(11).setTextColor(...COLORS.primaryRGB);
      pdf.text(title, 40, y); y += 14;
      pdf.setFont("helvetica", "normal").setFontSize(9).setTextColor(15, 23, 42);
      const lines = pdf.splitTextToSize(text, pageW - 80);
      lines.forEach(line => {
        if (y > 760) { pdf.addPage(); y = 50; }
        pdf.text(line, 40, y);
        y += 12;
      });
      y += 14;
    });
  }

  // footer page numbers
  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    pdf.setFont("helvetica", "normal").setFontSize(8).setTextColor(100, 116, 139);
    pdf.text(`Page ${i} of ${total}`, pageW - 40, 820, { align: "right" });
    pdf.text(r.code, 40, 820);
    pdf.text("capittance.com", pageW / 2, 820, { align: "center" });
  }

  pdf.save(`${safeName(r.title)}-${new Date().toISOString().split("T")[0]}.pdf`);
}

// ─── PowerPoint ─────────────────────────────────────────────────────────
export async function exportPPT(doc) {
  const r = buildReport(doc);
  const charts = await captureCharts();
  const ppt = new PptxGenJS();
  ppt.layout = "LAYOUT_WIDE"; // 13.333 × 7.5
  const k = r.kpis;
  const SLIDE_W = 13.333;
  const SLIDE_H = 7.5;

  // ─── Magazine cover slide ────────────────────────────────────────────
  const cover = ppt.addSlide();
  cover.background = { color: "0369A1" };
  // Right cyan accent stripe
  cover.addShape(ppt.ShapeType.rect, { x: SLIDE_W - 0.15, y: 0, w: 0.15, h: SLIDE_H, fill: { color: "06B6D4" }, line: { color: "06B6D4" } });
  // Top-left wordmark
  cover.addText("HARMONIZER", { x: 0.5, y: 0.4, w: 6, h: 0.35, fontSize: 12, bold: true, color: "FFFFFF", charSpacing: 4 });
  cover.addText("THE SAP DELIVERY OPERATING SYSTEM · capittance.com", { x: 0.5, y: 0.72, w: 8, h: 0.3, fontSize: 9, color: "BAE6FD", charSpacing: 1 });
  // Massive title
  cover.addText(r.title, { x: 0.5, y: 2.6, w: 12.3, h: 2.0, fontSize: 54, bold: true, color: "FFFFFF", valign: "top" });
  // Meta pills row
  cover.addText(`${r.code}   ·   ${k.methodology.toUpperCase()}   ·   ${k.phase.toUpperCase()}`,
    { x: 0.5, y: 4.7, w: 12, h: 0.4, fontSize: 12, bold: true, color: "67E8F9", charSpacing: 2 });
  // Bottom band
  cover.addShape(ppt.ShapeType.rect, { x: 0, y: SLIDE_H - 1.3, w: SLIDE_W, h: 1.3, fill: { color: "0F172A" }, line: { color: "0F172A" } });
  cover.addText("SPONSOR",           { x: 0.5,               y: SLIDE_H - 1.15, w: 4, h: 0.3, fontSize: 9, bold: true, color: "FFFFFF", charSpacing: 2 });
  cover.addText("PROJECT MANAGER",   { x: SLIDE_W / 3 + 0.2, y: SLIDE_H - 1.15, w: 4, h: 0.3, fontSize: 9, bold: true, color: "FFFFFF", charSpacing: 2 });
  cover.addText("GENERATED",         { x: (SLIDE_W / 3) * 2, y: SLIDE_H - 1.15, w: 4, h: 0.3, fontSize: 9, bold: true, color: "FFFFFF", charSpacing: 2 });
  cover.addText(k.sponsor, { x: 0.5,               y: SLIDE_H - 0.75, w: 4, h: 0.4, fontSize: 16, color: "BAE6FD" });
  cover.addText(k.pm,      { x: SLIDE_W / 3 + 0.2, y: SLIDE_H - 0.75, w: 4, h: 0.4, fontSize: 16, color: "BAE6FD" });
  cover.addText(todayStr(),{ x: (SLIDE_W / 3) * 2, y: SLIDE_H - 0.75, w: 4, h: 0.4, fontSize: 16, color: "BAE6FD" });

  // ─── KPI Infographic slide (6 tiles + budget bar) ────────────────────
  const kpi = ppt.addSlide();
  kpi.background = { color: "F8FAFC" };
  kpi.addShape(ppt.ShapeType.rect, { x: 0, y: 0, w: SLIDE_W, h: 0.9, fill: { color: "0369A1" }, line: { color: "0369A1" } });
  kpi.addText("EXECUTIVE SNAPSHOT", { x: 0.5, y: 0.2, w: 8, h: 0.3, fontSize: 10, bold: true, color: "FFFFFF", charSpacing: 3 });
  kpi.addText(r.title,               { x: 0.5, y: 0.4, w: 12, h: 0.4, fontSize: 20, bold: true, color: "FFFFFF" });

  const tiles = [
    { label: "OVERALL PROGRESS", value: `${k.progress}%`,                          sub: `${k.methodology} delivery`,       hex: "0369A1" },
    { label: "PROJECT HEALTH",   value: k.health.toUpperCase(),                     sub: `Phase · ${k.phase}`,             hex: healthHex(k.health) },
    { label: "OPEN RISKS",       value: String(k.openRisks),                        sub: `${k.criticalRisks} critical`,    hex: "DC2626" },
    { label: "REQUIREMENTS",     value: `${k.reqsDone}/${k.reqsTotal}`,             sub: `${pct(k.reqsDone, k.reqsTotal)}% complete`, hex: "2563EB" },
    { label: "OPEN ACTIONS",     value: String(k.actsOpen),                         sub: `${k.actsOverdue} overdue`,       hex: "D97706" },
    { label: "MILESTONES",       value: `${k.milestonesDone}/${k.milestonesTotal}`, sub: `${pct(k.milestonesDone, k.milestonesTotal)}% delivered`, hex: "16A34A" },
  ];
  const tileW = 4.0, tileH = 1.6, gap = 0.2;
  const rowY0 = 1.2;
  tiles.forEach((t, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = 0.5 + col * (tileW + gap);
    const y = rowY0 + row * (tileH + gap);
    // Card body
    kpi.addShape(ppt.ShapeType.roundRect, { x, y, w: tileW, h: tileH, fill: { color: "FFFFFF" }, line: { color: "E2E8F0", width: 0.5 }, rectRadius: 0.08 });
    // Accent bar left
    kpi.addShape(ppt.ShapeType.roundRect, { x, y, w: 0.08, h: tileH, fill: { color: t.hex }, line: { color: t.hex }, rectRadius: 0.03 });
    kpi.addText(t.label, { x: x + 0.25, y: y + 0.15, w: tileW - 0.35, h: 0.3, fontSize: 9,  bold: true, color: "64748B", charSpacing: 2 });
    kpi.addText(t.value, { x: x + 0.25, y: y + 0.45, w: tileW - 0.35, h: 0.75, fontSize: 30, bold: true, color: t.hex });
    kpi.addText(t.sub,   { x: x + 0.25, y: y + tileH - 0.4, w: tileW - 0.35, h: 0.3, fontSize: 10, color: "475569" });
  });
  // Budget row
  const barY = rowY0 + 2 * (tileH + gap) + 0.2;
  kpi.addText("BUDGET", { x: 0.5, y: barY, w: 4, h: 0.3, fontSize: 12, bold: true, color: "0F172A", charSpacing: 2 });
  kpi.addText(`Allocated ${fmtMoney(k.totalAlloc)}   ·   Spent ${fmtMoney(k.totalSpent)}   ·   Forecast ${fmtMoney(k.totalFcst)}   ·   Variance ${fmtMoney(k.variance)}`,
    { x: 0.5, y: barY + 0.3, w: 12.3, h: 0.3, fontSize: 10, color: "64748B" });
  // Bar background
  kpi.addShape(ppt.ShapeType.roundRect, { x: 0.5, y: barY + 0.65, w: 12.3, h: 0.25, fill: { color: "F0F9FF" }, line: { color: "F0F9FF" }, rectRadius: 0.05 });
  // Filled portion
  const spentPct = k.totalAlloc > 0 ? Math.min(1, k.totalSpent / k.totalAlloc) : 0;
  const fillHex = k.variance > 0 ? "DC2626" : "0369A1";
  if (spentPct > 0.005) {
    kpi.addShape(ppt.ShapeType.roundRect, { x: 0.5, y: barY + 0.65, w: 12.3 * spentPct, h: 0.25, fill: { color: fillHex }, line: { color: fillHex }, rectRadius: 0.05 });
  }

  // Footer
  kpi.addText(`Generated ${todayStr()}  ·  ${r.code || "—"}  ·  HARMONIZER · capittance.com`,
    { x: 0, y: SLIDE_H - 0.35, w: SLIDE_W, h: 0.3, fontSize: 8, color: "94A3B8", align: "center", charSpacing: 1 });

  // Executive summary
  const sum = ppt.addSlide();
  sum.addText("Executive Summary", { x: 0.5, y: 0.3, w: 12, h: 0.6, fontSize: 26, bold: true, color: "0369A1" });
  sum.addTable(
    [
      [{ text: "Field", options: { bold: true, color: "FFFFFF", fill: { color: "0369A1" } } },
       { text: "Value", options: { bold: true, color: "FFFFFF", fill: { color: "0369A1" } } }],
      ...r.summary.map(([k, v]) => [
        { text: String(k), options: { bold: true, color: "0F172A" } },
        { text: String(v), options: { color: "0F172A" } },
      ]),
    ],
    { x: 0.5, y: 1, w: 12, fontSize: 11, border: { type: "solid", pt: 0.5, color: "E0F2FE" } },
  );

  // Captured charts — one slide each, sized to fit the wide layout.
  charts.forEach((c, idx) => {
    const slide = ppt.addSlide();
    slide.addText(c.title || `Visual ${idx + 1}`, { x: 0.5, y: 0.3, w: 12, h: 0.6, fontSize: 22, bold: true, color: "0369A1" });
    // 13.333 wide × 7.5 tall layout — keep image inside 12.5 × 6.0 box.
    const boxW = 12.5;
    const boxH = 6.0;
    const ratio = c.w / c.h;
    let drawW = boxW;
    let drawH = drawW / ratio;
    if (drawH > boxH) { drawH = boxH; drawW = drawH * ratio; }
    const x = (13.333 - drawW) / 2;
    const y = 1.1 + (boxH - drawH) / 2;
    slide.addImage({ data: c.dataUrl, x, y, w: drawW, h: drawH });
  });

  // One slide per section (truncate to fit) — preceded by a divider
  r.sections.forEach((sec, i) => {
    if (!sec.rows.length) return;
    addPptSectionDivider(ppt, String(i + 1).padStart(2, "0"), sec.title, `${sec.rows.length} rows`);
    const slide = ppt.addSlide();
    slide.addText(sec.title, { x: 0.5, y: 0.3, w: 12, h: 0.6, fontSize: 24, bold: true, color: "0369A1" });
    const max = 14; // rows per slide for readability
    const visible = sec.rows.slice(0, max);
    const headerRow = sec.headers.map(h => ({
      text: h,
      options: { bold: true, color: "FFFFFF", fill: { color: "0369A1" }, fontSize: 10 },
    }));
    const bodyRows = visible.map(row => row.map(cell => ({
      text: String(cell ?? ""),
      options: { color: "0F172A", fontSize: 9 },
    })));
    const footer = sec.totals ? [sec.totals.map(c => ({
      text: String(c ?? ""),
      options: { bold: true, color: "0369A1", fill: { color: "F0F9FF" }, fontSize: 10 },
    }))] : [];
    slide.addTable([headerRow, ...bodyRows, ...footer], {
      x: 0.4, y: 1, w: 12.6, fontSize: 9,
      border: { type: "solid", pt: 0.5, color: "E0F2FE" },
      autoPage: false,
    });
    if (sec.rows.length > max) {
      slide.addText(`+ ${sec.rows.length - max} more rows — see PDF/Word export for full data`,
        { x: 0.5, y: 6.9, w: 12, h: 0.3, fontSize: 9, italic: true, color: "64748B" });
    }
  });

  // AI insights
  r.aiNarratives.forEach(([title, text]) => {
    const slide = ppt.addSlide();
    slide.addText("Insight — " + title, { x: 0.5, y: 0.3, w: 12, h: 0.6, fontSize: 22, bold: true, color: "0369A1" });
    slide.addText(text, { x: 0.6, y: 1.1, w: 12.2, h: 5.8, fontSize: 14, color: "0F172A", valign: "top" });
  });

  ppt.writeFile({ fileName: `${safeName(r.title)}-${new Date().toISOString().split("T")[0]}.pptx` });
}

// ─── Word ─────────────────────────────────────────────────────────────────
export async function exportWord(doc) {
  const r = buildReport(doc);
  const charts = await captureCharts();

  const heading = (text, level = HeadingLevel.HEADING_1) =>
    new Paragraph({
      heading: level, spacing: { before: 240, after: 120 },
      children: [new TextRun({ text, color: "0369A1", bold: true })],
    });

  const para = (text, opts = {}) =>
    new Paragraph({
      spacing: { after: 100 },
      children: [new TextRun({ text, ...opts })],
    });

  const cell = (text, opts = {}) => new TableCell({
    children: [new Paragraph({ children: [new TextRun({ text: String(text ?? ""), size: 18, ...opts })] })],
    width: { size: opts.size || 100 / 6, type: WidthType.PERCENTAGE },
  });

  const headerCell = (text) => new TableCell({
    shading: { fill: "0369A1" },
    children: [new Paragraph({ children: [new TextRun({ text: String(text), color: "FFFFFF", bold: true, size: 18 })] })],
  });

  const buildTable = (headers, rows, totals) => {
    const head = new TableRow({ children: headers.map(headerCell), tableHeader: true });
    const body = rows.map(row => new TableRow({ children: row.map(c => cell(c)) }));
    const foot = totals
      ? [new TableRow({ children: totals.map(c => cell(c, { bold: true })) })]
      : [];
    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top:    { style: BorderStyle.SINGLE, size: 4, color: "E0F2FE" },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: "E0F2FE" },
        left:   { style: BorderStyle.SINGLE, size: 4, color: "E0F2FE" },
        right:  { style: BorderStyle.SINGLE, size: 4, color: "E0F2FE" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: "E0F2FE" },
        insideVertical:   { style: BorderStyle.SINGLE, size: 4, color: "E0F2FE" },
      },
      rows: [head, ...body, ...foot],
    });
  };

  const k = r.kpis;

  // ─── Word cover section ────────────────────────────────────────────────
  // Word doesn't natively support full-page banded covers, but we can build a
  // striking title block with an ocean-band table, big title, and KPI grid so
  // it still feels like a magazine open, not a memo.
  const bandCell = (text, opts = {}) => new TableCell({
    shading: { fill: opts.fill || "0369A1" },
    margins: { top: 180, bottom: 180, left: 240, right: 240 },
    children: [new Paragraph({ alignment: opts.align || AlignmentType.LEFT,
      children: [new TextRun({ text: String(text), color: opts.color || "FFFFFF", bold: opts.bold !== false, size: opts.size || 18 })] })],
  });
  const kpiCell = (label, value, sub, fill) => new TableCell({
    shading: { fill: "FFFFFF" },
    margins: { top: 120, bottom: 120, left: 200, right: 200 },
    borders: {
      top:    { style: BorderStyle.SINGLE, size: 4, color: "E2E8F0" },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: "E2E8F0" },
      left:   { style: BorderStyle.SINGLE, size: 24, color: fill || "0369A1" },
      right:  { style: BorderStyle.SINGLE, size: 4, color: "E2E8F0" },
    },
    children: [
      new Paragraph({ spacing: { after: 60 }, children: [new TextRun({ text: label, bold: true, size: 14, color: "64748B" })] }),
      new Paragraph({ spacing: { after: 60 }, children: [new TextRun({ text: value, bold: true, size: 44, color: fill || "0369A1" })] }),
      new Paragraph({ children: [new TextRun({ text: sub, size: 16, color: "475569" })] }),
    ],
  });

  const coverBand = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
      left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
      insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
    },
    rows: [
      new TableRow({ children: [bandCell("HARMONIZER  ·  THE SAP DELIVERY OPERATING SYSTEM  ·  capittance.com", { size: 16 })] }),
      new TableRow({ children: [bandCell(r.title.toUpperCase(), { size: 44, fill: "0369A1" })] }),
      new TableRow({ children: [bandCell(`${r.code || "—"}   ·   ${k.methodology.toUpperCase()}   ·   ${k.phase.toUpperCase()}   ·   Generated ${todayStr()}`, { size: 18, color: "BAE6FD", fill: "0F172A" })] }),
    ],
  });

  const kpiGrid = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
      left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
      insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
    },
    rows: [
      new TableRow({ children: [
        kpiCell("OVERALL PROGRESS", `${k.progress}%`,                  `${k.methodology} delivery`,       "0369A1"),
        kpiCell("PROJECT HEALTH",   k.health.toUpperCase(),             `Phase · ${k.phase}`,              healthHex(k.health)),
        kpiCell("OPEN RISKS",       String(k.openRisks),                `${k.criticalRisks} critical`,     "DC2626"),
      ]}),
      new TableRow({ children: [
        kpiCell("REQUIREMENTS",     `${k.reqsDone}/${k.reqsTotal}`,     `${pct(k.reqsDone, k.reqsTotal)}% complete`, "2563EB"),
        kpiCell("OPEN ACTIONS",     String(k.actsOpen),                 `${k.actsOverdue} overdue`,        "D97706"),
        kpiCell("MILESTONES",       `${k.milestonesDone}/${k.milestonesTotal}`, `${pct(k.milestonesDone, k.milestonesTotal)}% delivered`, "16A34A"),
      ]}),
    ],
  });

  const children = [
    coverBand,
    new Paragraph({ spacing: { before: 320, after: 120 },
      children: [new TextRun({ text: "EXECUTIVE SNAPSHOT", bold: true, size: 16, color: "0369A1", characterSpacing: 40 })] }),
    kpiGrid,
    new Paragraph({ spacing: { before: 360, after: 120 }, children: [new TextRun({ text: `Sponsor: ${k.sponsor}   ·   PM: ${k.pm}   ·   Budget: ${fmtMoney(k.totalAlloc)}   ·   Forecast: ${fmtMoney(k.totalFcst)}   ·   Variance: ${fmtMoney(k.variance)}`, size: 18, color: "475569" })] }),

    heading("Executive Summary", HeadingLevel.HEADING_1),
    buildTable(["Field", "Value"], r.summary),
  ];

  // Captured charts — Word lets us inline images so each gets its own heading.
  if (charts.length) {
    children.push(heading("Visual Highlights", HeadingLevel.HEADING_1));
    for (const c of charts) {
      children.push(heading(c.title, HeadingLevel.HEADING_2));
      // Strip the base64 prefix and convert to a Uint8Array for docx ImageRun.
      const base64 = c.dataUrl.split(",")[1];
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      // Word page is ~600pt wide. Scale image proportionally.
      const maxW = 600;
      const drawW = Math.min(c.w, maxW);
      const drawH = (c.h / c.w) * drawW;
      children.push(new Paragraph({
        spacing: { after: 200 },
        children: [new ImageRun({ data: bytes, transformation: { width: drawW, height: drawH } })],
      }));
    }
  }

  r.sections.forEach((sec, i) => {
    if (!sec.rows.length) return;
    // Section divider: numeric tag + rule line, mirrors PDF/PPT dividers.
    children.push(new Paragraph({
      spacing: { before: 480, after: 60 },
      children: [new TextRun({ text: String(i + 1).padStart(2, "0"), bold: true, size: 72, color: "0369A1" })],
    }));
    children.push(new Paragraph({
      spacing: { after: 40 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: "0369A1", space: 1 } },
      children: [new TextRun({ text: " ", size: 4 })],
    }));
    children.push(heading(sec.title, HeadingLevel.HEADING_1));
    children.push(buildTable(sec.headers, sec.rows, sec.totals));
  });

  if (r.aiNarratives.length) {
    children.push(heading("Executive Insights", HeadingLevel.HEADING_1));
    r.aiNarratives.forEach(([title, text]) => {
      children.push(heading(title, HeadingLevel.HEADING_2));
      children.push(para(text));
    });
  }

  const wd = new Document({
    creator: "Harmonizer · capittance.com",
    title: r.title,
    description: "Project report",
    sections: [{ properties: {}, children }],
  });

  const blob = await Packer.toBlob(wd);
  saveAs(blob, `${safeName(r.title)}-${new Date().toISOString().split("T")[0]}.docx`);
}
